import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import TimeEntryEditModal from '../components/TimeEntryEditModal';
import TodoPanel from '../components/TodoPanel';
import { ListTodo, Trash2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// ===== UTILITY FUNCTIONS FOR LOCAL DATE HANDLING =====

// Get local date string in YYYY-MM-DD format using USER'S LOCAL TIMEZONE
// Uses getFullYear(), getMonth(), getDate() which respect local timezone
const getLocalDateString = (date) => {
  const year = date.getFullYear();        // Local year
  const month = String(date.getMonth() + 1).padStart(2, '0');  // Local month (0-11, so +1)
  const day = String(date.getDate()).padStart(2, '0');         // Local day
  return `${year}-${month}-${day}`; // e.g., "2025-10-20" in user's timezone
};

// Create a Date object at midnight local time (NO timezone conversion)
const createLocalDate = (year, month, day) => {
  return new Date(year, month, day, 0, 0, 0, 0);
};

// Get start of week (Sunday) for a given date
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return createLocalDate(d.getFullYear(), d.getMonth(), diff);
};

function Calendar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize with TODAY at midnight local time
  const today = new Date();
  const todayMidnight = createLocalDate(today.getFullYear(), today.getMonth(), today.getDate());
  
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(todayMidnight));
  const [selectedDate, setSelectedDate] = useState(todayMidnight);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefilledData, setPrefilledData] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showTodoPanel, setShowTodoPanel] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Store entry to delete
  const [todoFocusId, setTodoFocusId] = useState(null);

  // Fetch entries when component mounts
  useEffect(() => {
    if (user) {
      fetchEntries();
    }

    // Listen for time entries update event (from notes linking, etc.)
    const handleTimeEntriesUpdated = () => {
      fetchEntries();
    };

    window.addEventListener('timeEntriesUpdated', handleTimeEntriesUpdated);

    return () => {
      window.removeEventListener('timeEntriesUpdated', handleTimeEntriesUpdated);
    };
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const todoParam = params.get('todo');
    const entryParam = params.get('entry');

    // Handle todo panel
    if (todoParam) {
      setShowTodoPanel(true);

      if (todoParam !== 'panel') {
        setTodoFocusId(todoParam);
      }

      params.delete('todo');
      const newSearch = params.toString();
      navigate(
        newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
        { replace: true }
      );
    }

    // Handle time entry selection
    if (entryParam) {
      // Find the entry and open edit modal
      const entry = entries.find(e => e.id === entryParam);
      if (entry) {
        setEditingEntry(entry);
        setShowModal(true);
        setPrefilledData(null);
      }

      params.delete('entry');
      const newSearch = params.toString();
      navigate(
        newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
        { replace: true }
      );
    }
  }, [location.search, location.pathname, navigate, entries]);

  // Log when entries state changes (debugging)
  useEffect(() => {
    console.log('🔔 Entries state changed! Total entries:', entries.length);
    if (entries.length > 0) {
      const selectedKey = getLocalDateString(selectedDate);
      const todayEntries = entries.filter(e => {
        const eDate = new Date(e.date);
        return getLocalDateString(eDate) === selectedKey;
      });
      console.log(`  → ${todayEntries.length} entries for selected date (${selectedKey})`);
      
      // Log distribution by hour
      const byHour = {};
      todayEntries.forEach(e => {
        const hour = new Date(e.date).getHours();
        byHour[hour] = (byHour[hour] || 0) + 1;
      });
      console.log('  → Distribution by hour:', byHour);
    }
  }, [entries, selectedDate]);

  // Fetch entries from API with cache busting
  const fetchEntries = async () => {
    console.log('📅 Fetching all entries from backend...');
    setLoading(true);
    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/time-entries?_t=${timestamp}`);
      const fetchedEntries = response.data;
      
      console.log(`✅ Fetched ${fetchedEntries.length} total entries`);
      
      if (fetchedEntries.length > 0) {
        const sample = fetchedEntries[0];
        const sampleDate = new Date(sample.date);
        console.log('Sample entry:', {
          id: sample.id?.substring(0, 8),
          date: sample.date,
          dateKey: getLocalDateString(sampleDate),
          hour: sampleDate.getHours(),
          start_hour: sample.start_hour,
          project: sample.project_name
        });
      }
      
      // Force state update with fresh entries - use functional update to ensure latest state
      setEntries(prevEntries => {
        console.log(`🔄 Updating state: ${prevEntries.length} → ${fetchedEntries.length} entries`);
        return fetchedEntries;
      });
      
      console.log('✅ State updated successfully');
    } catch (error) {
      console.error('❌ Error fetching entries:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load entries. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  // Get 7 days of current week
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // REVERT: Get entries that START in a specific hour (not span through it)
  const getEntriesForHour = (hour) => {
    const dayKey = getLocalDateString(selectedDate);
    
    const filtered = entries.filter(entry => {
      // Extract date from entry.date - handle ISO format to avoid timezone shifting
      let entryDayKey;
      if (entry.date && typeof entry.date === 'string') {
        // Extract date part from ISO string (YYYY-MM-DD) to avoid timezone conversion
        if (entry.date.includes('T')) {
          const datePart = entry.date.split('T')[0];
          entryDayKey = datePart; // Already in YYYY-MM-DD format
        } else {
          // Already a date-only string
          entryDayKey = entry.date;
        }
      } else {
        // Fallback to date parsing
        const entryDate = new Date(entry.date);
        entryDayKey = getLocalDateString(entryDate);
      }
      
      // Day must match
      if (entryDayKey !== dayKey) return false;
      
      // Only show entries that START in this hour
      // start_hour is stored as the local hour provided by the user
      if (Number.isInteger(entry.start_hour)) {
        return entry.start_hour === hour;
      }
      
      // Fallback: Infer from local timestamp (rare, for legacy entries)
      const entryDate = new Date(entry.date);
      const localHour = entryDate.getHours();
      return localHour === hour;
    });
    
    return filtered;
  };

  // Week navigation - maintains local date
  const goToPreviousWeek = () => {
    const prevWeek = new Date(currentWeekStart);
    prevWeek.setDate(prevWeek.getDate() - 7);
    setCurrentWeekStart(prevWeek);
    
    const newSelected = new Date(selectedDate);
    newSelected.setDate(selectedDate.getDate() - 7);
    setSelectedDate(newSelected);
  };

  const goToNextWeek = () => {
    const nextWeek = new Date(currentWeekStart);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setCurrentWeekStart(nextWeek);
    
    const newSelected = new Date(selectedDate);
    newSelected.setDate(selectedDate.getDate() + 7);
    setSelectedDate(newSelected);
  };

  const goToToday = () => {
    const now = new Date();
    const todayLocal = createLocalDate(now.getFullYear(), now.getMonth(), now.getDate());
    setCurrentWeekStart(getWeekStart(todayLocal));
    setSelectedDate(todayLocal);
  };

  // Handle clicking a date in the week view - use LOCAL midnight
  const handleDateClick = (date) => {
    const localDate = createLocalDate(date.getFullYear(), date.getMonth(), date.getDate());
    setSelectedDate(localDate);
    console.log('📅 Selected date:', getLocalDateString(localDate));
  };

  // Handle entry edit - open modal with entry data
  const handleEditEntry = (entry) => {
    console.log('📝 Editing entry:', entry);
    setEditingEntry(entry);
    setPrefilledData(null); // Clear prefilled data when editing
    setShowModal(true);
  };

  // Handle entry creation - NO TIMEZONE CONVERSION
  const handleCreateEntry = (hour) => {
    console.log('=== CREATE ENTRY CLICKED ===');
    console.log('Hour:', hour);
    console.log('Selected date:', selectedDate);
    
    // Format time in 24-hour format
    const startTime = `${String(hour).padStart(2, '0')}:00`;
    const endTime = `${String((hour + 1) % 24).padStart(2, '0')}:00`;
    
    // Get date string using LOCAL date components (NO .toISOString()!)
    const dateStr = getLocalDateString(selectedDate);
    
    console.log('📝 Prefill data:');
    console.log('  Date:', dateStr);
    console.log('  Start time:', startTime);
    console.log('  End time:', endTime);
    console.log('  Hour:', hour);
    
    setPrefilledData({
      date: dateStr,
      start_time: startTime,
      end_time: endTime,
      duration_minutes: 60,
      entry_type: 'calendar'
    });
    setShowModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
    setPrefilledData(null);
    setEditingEntry(null);
  };

  // STEP 2: Handle modal complete - use created entry directly if provided
  const handleModalComplete = async (createdEntry) => {
    console.log('[STEP 2] Modal complete called');
    
    // Close modal first
    setShowModal(false);
    setPrefilledData(null);
    setEditingEntry(null);
    
    if (createdEntry) {
      // STEP 2: Immediately append to state
      console.log('[STEP 2] Adding created entry to state:', {
        id: createdEntry.id.substring(0, 8),
        start_hour: createdEntry.start_hour,
        end_hour: createdEntry.end_hour,
        date: createdEntry.date
      });
      
      setEntries(prev => [...prev, createdEntry]);
      toast.success('Entry added to calendar!');
    } else {
      // Fallback: refetch all entries (e.g., if editing)
      console.log('[STEP 2] No created entry provided, refetching...');
      await new Promise(resolve => setTimeout(resolve, 300));
      await fetchEntries();
      toast.success('Calendar updated!');
    }
  };

  // Handle entry deletion with confirmation
  const handleDeleteEntry = async (entryId) => {
    console.log('🗑️ Deleting entry:', entryId);
    try {
      await axios.delete(`${API}/time-entries/${entryId}`);
      
      // Remove from state immediately
      setEntries(prev => prev.filter(e => e.id !== entryId));
      
      // Close confirmation dialog
      setDeleteConfirm(null);
      
      toast.success('Entry deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting entry:', error);
      toast.error('Failed to delete entry. Please try again.');
    }
  };

  const weekDays = getWeekDays();
  
  // Get all entries for selected date (for display count) using LOCAL date
  const selectedDateKey = getLocalDateString(selectedDate);
  const dayEntries = entries.filter(entry => {
    const entryDate = new Date(entry.date);
    const entryDateKey = getLocalDateString(entryDate);
    return entryDateKey === selectedDateKey;
  });
  
  console.log(`📅 Rendering Calendar for ${selectedDateKey}, ${dayEntries.length} entries`);

  // Show loading if user not ready
  if (!user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', marginTop: '50px' }}>
        <p>Loading user information...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 transition-colors" style={{ padding: '20px 20px 100px 20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div className="mb-5 p-5 bg-gradient-to-r from-indigo-500 to-purple-500 dark:from-indigo-600 dark:to-purple-600 rounded-xl text-white transition-colors" style={{ 
        marginBottom: '20px',
        padding: '20px',
        borderRadius: '12px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h1 style={{ margin: '0', fontSize: '28px', fontWeight: '600' }}>
            Calendar
          </h1>
          <button
            onClick={() => setShowTodoPanel(!showTodoPanel)}
            className={`px-4 py-2.5 border rounded-lg cursor-pointer font-semibold flex items-center gap-2 transition-colors ${
              showTodoPanel 
                ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 border-white dark:border-gray-700' 
                : 'bg-white/20 dark:bg-white/10 text-white border-white/30 dark:border-white/20 hover:bg-white/30 dark:hover:bg-white/20'
            }`}
          >
            <ListTodo size={20} />
            To-Do List
          </button>
        </div>
        
        {/* Week Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '15px',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <button 
            onClick={goToPreviousWeek}
            className="px-4 py-2 bg-white/20 dark:bg-white/10 text-white border border-white/30 dark:border-white/20 rounded-md cursor-pointer font-medium hover:bg-white/30 dark:hover:bg-white/20 transition-colors"
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            ← Previous Week
          </button>
          <button 
            onClick={goToToday}
            className="px-5 py-2 bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 border-none rounded-md cursor-pointer font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Today
          </button>
          <button 
            onClick={goToNextWeek}
            className="px-4 py-2 bg-white/20 dark:bg-white/10 text-white border border-white/30 dark:border-white/20 rounded-md cursor-pointer font-medium hover:bg-white/30 dark:hover:bg-white/20 transition-colors"
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Next Week →
          </button>
        </div>

        {/* Week Days */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
          {weekDays.map((day, index) => {
            const dayKey = getLocalDateString(day);
            const selectedKey = getLocalDateString(selectedDate);
            const todayKey = getLocalDateString(new Date());
            
            const isSelected = dayKey === selectedKey;
            const isToday = dayKey === todayKey;
            
            return (
              <button
                key={index}
                onClick={() => handleDateClick(day)}
                className={`px-2 py-3 rounded-lg cursor-pointer text-[13px] text-center transition-all ${
                  isSelected 
                    ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 font-bold' 
                    : 'bg-white/15 dark:bg-white/10 text-white font-medium'
                } ${isToday ? 'border-2 border-white dark:border-gray-300' : ''}`}
                style={{
                  padding: '12px 8px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'center',
                  transition: 'all 0.2s'
                }}
              >
                <div>{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div style={{ fontSize: '18px', marginTop: '4px' }}>
                  {day.getDate()}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Date Info */}
      <div className="p-4 bg-blue-50 dark:bg-gray-800 rounded-lg mb-5 text-center transition-colors" style={{
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        <div className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100 transition-colors" style={{ fontWeight: '600', fontSize: '16px', marginBottom: '5px' }}>
          {selectedDate.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
        <div className="text-gray-600 dark:text-gray-400 transition-colors">
          {dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'} on this day
        </div>
      </div>

      {/* Time Slots */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[...Array(24)].map((_, hour) => {
          const hourEntries = getEntriesForHour(hour);
          const hourLabel = hour === 0 ? '12 AM' :
                           hour < 12 ? `${hour} AM` :
                           hour === 12 ? '12 PM' :
                           `${hour - 12} PM`;
          
          // FIXED SLOT HEIGHT for calculations
          const SLOT_HEIGHT = 80; // pixels
          const SLOT_GAP = 10; // gap between slots
          
          return (
            <div 
              key={hour} 
              className="flex border border-gray-200 dark:border-gray-700 rounded-lg overflow-visible relative transition-colors"
              style={{
                display: 'flex',
                borderRadius: '8px',
                height: `${SLOT_HEIGHT}px`,
                overflow: 'visible',
                position: 'relative'
              }}
            >
              {/* Time Label */}
              <div className="w-[120px] p-4 bg-gray-50 dark:bg-gray-800 flex items-center justify-center font-semibold text-sm text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 transition-colors" style={{
                width: '120px',
                padding: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px',
                borderRight: '1px solid'
              }}>
                {hourLabel}
              </div>
              
              {/* Time Slot Content */}
              <div 
                className="flex-1 p-0 bg-white dark:bg-gray-800 relative transition-colors"
                style={{
                  flex: 1,
                  padding: '0',
                  position: 'relative'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.background = document.documentElement.classList.contains('dark') ? '#1e293b' : '#f0f9ff';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.background = document.documentElement.classList.contains('dark') ? '#1f2937' : 'white';
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.currentTarget.style.background = document.documentElement.classList.contains('dark') ? '#1f2937' : 'white';
                  
                  if (draggedTask) {
                    // Create time entry from task
                    setPrefilledData({
                      date: selectedDate,
                      hour: hour,
                      notes: draggedTask.title,
                      taskId: draggedTask.id
                    });
                    setShowModal(true);
                    setDraggedTask(null);
                  }
                }}
              >
                {hourEntries.length > 0 ? (
                  hourEntries.map(entry => {
                    // Calculate height based on duration - accounts for gaps between slots
                    const durationHours = entry.duration_minutes / 60;
                    const entryHeight = (durationHours * SLOT_HEIGHT) + ((durationHours - 1) * SLOT_GAP);
                    
                    console.log(`📏 Entry ${entry.id?.substring(0, 8)}: ${entry.duration_minutes}min = ${durationHours}h = ${entryHeight}px`);
                    
                    return (
                      <div 
                        key={entry.id} 
                        onClick={() => handleEditEntry(entry)}
                        className="p-4 rounded-xl border cursor-pointer transition-all relative z-10 overflow-hidden dark:bg-gray-700/75 dark:border-gray-600"
                        style={{
                          padding: '16px',
                          backdropFilter: 'blur(12px) saturate(120%)',
                          WebkitBackdropFilter: 'blur(12px) saturate(120%)',
                          borderRadius: '12px',
                          border: '1px solid',
                          borderLeft: `3px solid ${entry.project_color || '#3b82f6'}`,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                          transition: 'all 0.2s ease',
                          height: `${entryHeight}px`,
                          minHeight: `${entryHeight}px`,
                          width: 'calc(100% - 20px)',
                          margin: '10px',
                          background: document.documentElement.classList.contains('dark') 
                            ? 'rgba(31, 41, 55, 0.75)' 
                            : 'rgba(255, 255, 255, 0.75)'
                        }}
                        onMouseEnter={(e) => {
                          const isDark = document.documentElement.classList.contains('dark');
                          e.currentTarget.style.background = isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.85)';
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.borderLeft = `3px solid ${entry.project_color || '#3b82f6'}`;
                        }}
                        onMouseLeave={(e) => {
                          const isDark = document.documentElement.classList.contains('dark');
                          e.currentTarget.style.background = isDark ? 'rgba(31, 41, 55, 0.75)' : 'rgba(255, 255, 255, 0.75)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderLeft = `3px solid ${entry.project_color || '#3b82f6'}`;
                        }}
                      >
                        {/* Delete Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent edit modal from opening
                            setDeleteConfirm(entry);
                          }}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            background: 'rgba(248, 113, 113, 0.7)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                            zIndex: 10,
                            opacity: 0.7
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.95)';
                            e.currentTarget.style.transform = 'scale(1.08)';
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(248, 113, 113, 0.7)';
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.opacity = '0.7';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.08)';
                          }}
                        >
                          <Trash2 size={13} color="white" />
                        </button>
                        
                        {/* Entry Content */}
                        <div className="font-bold mb-1.5 text-blue-800 dark:text-blue-300 text-[15px] transition-colors" style={{ fontWeight: '700', marginBottom: '6px', fontSize: '15px' }}>
                          {entry.project_name || 'No Project'}
                        </div>
                        {entry.notes && (
                          <div className="text-sm text-gray-800 dark:text-gray-200 mb-1.5 opacity-90 font-medium transition-colors" style={{ fontSize: '14px', marginBottom: '6px', opacity: 0.9, fontWeight: '500' }}>
                            {entry.notes}
                          </div>
                        )}
                        <div className="text-xs text-gray-600 dark:text-gray-400 opacity-80 font-semibold transition-colors" style={{ fontSize: '12px', opacity: 0.8, fontWeight: '600' }}>
                          {entry.duration_minutes} minutes
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <button 
                    onClick={() => handleCreateEntry(hour)}
                    className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer text-sm text-gray-600 dark:text-gray-400 font-medium m-2.5 transition-colors hover:bg-gray-200 dark:hover:bg-gray-600"
                    style={{
                      padding: '10px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      width: 'fit-content',
                      fontSize: '14px',
                      fontWeight: '500',
                      margin: '10px'
                    }}
                  >
                    + Add Entry
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div style={{
          position: 'fixed',
          bottom: '80px',
          right: '20px',
          padding: '15px 25px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          borderRadius: '8px',
          fontWeight: '500'
        }}>
          Loading entries...
        </div>
      )}

      {/* Entry Creation/Edit Modal */}
      {showModal && (
        <TimeEntryEditModal
          open={showModal}
          entry={editingEntry}
          prefilledData={prefilledData}
          onClose={handleModalClose}
          onComplete={handleModalComplete}
        />
      )}

      {/* Todo Panel */}
      <TodoPanel
        isOpen={showTodoPanel}
        onClose={() => setShowTodoPanel(false)}
        selectedWeek={getLocalDateString(currentWeekStart)}
        onTaskDragStart={(e, task) => {
          setDraggedTask(task);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onCreateEntry={(task) => {
          // Open time entry modal prefilled with task title
          const today = new Date();
          setPrefilledData({
            date: createLocalDate(today.getFullYear(), today.getMonth(), today.getDate()),
            hour: today.getHours(),
            notes: task.title,
            taskId: task.id
          });
          setShowModal(true);
        }}
        initialTaskId={todoFocusId}
        onInitialTaskHandled={() => setTodoFocusId(null)}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.3s ease'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
              margin: '0 auto 20px'
            }}>
              <Trash2 size={28} color="#dc2626" />
            </div>
            
            <h3 style={{ 
              margin: '0 0 12px', 
              fontSize: '22px', 
              fontWeight: '700',
              textAlign: 'center',
              color: '#1f2937'
            }}>
              Delete Entry?
            </h3>
            
            <p style={{ 
              margin: '0 0 8px', 
              color: '#6b7280', 
              fontSize: '15px',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              Are you sure you want to delete this time entry?
            </p>
            
            <div style={{
              background: '#f9fafb',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '24px'
            }}>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px', color: '#1f2937' }}>
                {deleteConfirm.project_name || 'No Project'}
              </div>
              {deleteConfirm.notes && (
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                  {deleteConfirm.notes}
                </div>
              )}
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                {deleteConfirm.duration_minutes} minutes
              </div>
            </div>
            
            <p style={{ 
              margin: '0 0 24px', 
              color: '#dc2626', 
              fontSize: '13px',
              textAlign: 'center',
              fontWeight: '500'
            }}>
              This action cannot be undone.
            </p>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '15px',
                  color: '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteEntry(deleteConfirm.id)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '15px',
                  color: 'white',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 38, 38, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                }}
              >
                Delete Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNav active="calendar" />
    </div>
  );
}

export default Calendar;